import { useEffect, useMemo, useState } from 'react';
import { Badge, Button, Card, Form, InputGroup, Table } from 'react-bootstrap';
import {
	FaAngleDoubleDown,
	FaAngleDoubleUp,
	FaChevronDown,
	FaChevronRight,
	FaSearch,
} from 'react-icons/fa';
import { useTranslation } from 'react-i18next';
import { formatCurrency } from 'src/utils';
import type { OptionalCatalogItem } from '../types';

interface AutovistaOptionalsPickerProps {
	catalog: OptionalCatalogItem[];
	value: OptionalCatalogItem[];
	onChange: (next: OptionalCatalogItem[]) => void;
}

/** Oltre questa soglia i gruppi partono chiusi: la lista si legge dall'indice. */
const COLLAPSE_THRESHOLD = 40;

interface CatalogGroup {
	name: string;
	items: OptionalCatalogItem[];
}

/** La famiglia e' gia' nell'intestazione del gruppo: qui resta il resto. */
const subtitleOf = (item: OptionalCatalogItem): string => {
	const parts = [item.descrizioneNormalizzata, item.descrizioneBreve]
		.filter(Boolean)
		.filter((p) => p !== item.descrizione);
	return [...new Set(parts)].join(' · ');
};

const haystackOf = (item: OptionalCatalogItem): string =>
	[
		item.codice,
		item.descrizione,
		item.descrizioneBreve,
		item.descrizioneNormalizzata,
		item.descrizioneEquipaggiamento,
	]
		.filter(Boolean)
		.join(' ')
		.toLowerCase();

/**
 * Gemello ES di `MotornetOptionalsPicker`: Autovista non pubblica vincoli fra
 * equipaggiamenti, quindi niente validazione, badge o righe bloccate. In cambio
 * i listini ES arrivano a centinaia di voci, percio' qui c'e' la ricerca e il
 * raggruppamento per famiglia ESACO (`descrizioneEquipaggiamento`).
 */
const AutovistaOptionalsPicker = ({
	catalog,
	value,
	onChange,
}: AutovistaOptionalsPickerProps) => {
	const { t } = useTranslation();
	const [query, setQuery] = useState('');
	/** Solo le aperture/chiusure decise dall'utente: il resto e' derivato. */
	const [toggles, setToggles] = useState<Record<string, boolean>>({});

	// Cambio veicolo: la ricerca e i gruppi aperti non hanno piu' senso.
	useEffect(() => {
		setQuery('');
		setToggles({});
	}, [catalog]);

	const selected = useMemo(
		() => new Set(value.map((o) => o.idAccessorio)),
		[value]
	);

	const otherLabel = t('other', 'Altro');
	const groups = useMemo<CatalogGroup[]>(() => {
		const needle = query.trim().toLowerCase();
		const byName = new Map<string, OptionalCatalogItem[]>();
		for (const item of catalog) {
			if (needle && !haystackOf(item).includes(needle)) continue;
			const name = item.descrizioneEquipaggiamento?.trim() || otherLabel;
			const bucket = byName.get(name);
			if (bucket) bucket.push(item);
			else byName.set(name, [item]);
		}
		return [...byName.entries()]
			.map(([name, items]) => ({
				name,
				// dentro il gruppo le voci a pagamento vengono prima: sono quelle
				// che l'operatore deve davvero scegliere.
				items: [
					...items.filter((i) => !i.isStandard),
					...items.filter((i) => i.isStandard),
				],
			}))
			.sort((a, b) => a.name.localeCompare(b.name));
	}, [catalog, query, otherLabel]);

	const visible = useMemo(() => groups.flatMap((g) => g.items), [groups]);
	const isFiltering = query.trim().length > 0;

	/**
	 * Un gruppo e' aperto se lo si sta cercando, se contiene una scelta o se il
	 * listino e' corto abbastanza da stare tutto sotto gli occhi.
	 */
	const isExpanded = (group: CatalogGroup): boolean => {
		const manual = toggles[group.name];
		if (manual !== undefined) return manual;
		if (isFiltering || groups.length === 1) return true;
		if (catalog.length <= COLLAPSE_THRESHOLD) return true;
		return group.items.some((i) => selected.has(i.idAccessorio));
	};

	const allExpanded = groups.length > 0 && groups.every(isExpanded);

	/** Apre o chiude in blocco: l'override esplicito vale per tutti i gruppi. */
	const setAllExpanded = (expanded: boolean) =>
		setToggles(Object.fromEntries(groups.map((g) => [g.name, expanded])));

	const toggle = (item: OptionalCatalogItem, checked: boolean) => {
		if (checked) {
			onChange([...value, item]);
		} else {
			onChange(value.filter((o) => o.idAccessorio !== item.idAccessorio));
		}
	};

	/** Spunta o svuota un insieme di voci in un colpo solo. */
	const toggleMany = (items: OptionalCatalogItem[], checked: boolean) => {
		const ids = new Set(items.map((i) => i.idAccessorio));
		const kept = value.filter((o) => !ids.has(o.idAccessorio));
		onChange(checked ? [...kept, ...items] : kept);
	};

	const selectedTotal = value.reduce((sum, o) => sum + (o.prezzo ?? 0), 0);

	if (!catalog.length) {
		return (
			<Card className="mt-2">
				<Card.Body className="text-muted">
					{t('motornet_no_optionals', 'Nessun optional disponibile')}
				</Card.Body>
			</Card>
		);
	}

	return (
		<Card className="mt-2">
			<Card.Body>
				<div className="d-flex flex-wrap align-items-center gap-3 mb-3">
					<InputGroup style={{ maxWidth: '22rem' }}>
						<InputGroup.Text>
							<FaSearch />
						</InputGroup.Text>
						<Form.Control
							type="search"
							value={query}
							onChange={(e) => setQuery(e.target.value)}
							placeholder={`${t('search', 'Cerca')} (${t(
								'code',
								'Codice'
							)} / ${t('description', 'Descrizione')})`}
							aria-label={t('search', 'Cerca')}
						/>
					</InputGroup>

					<div className="ms-auto d-flex align-items-center gap-2 small text-muted">
						<span>
							{t('motornet_opt_selected_count', {
								defaultValue: '{{count}} di {{total}} selezionati',
								count: value.length,
								total: catalog.length,
							})}
						</span>
						{selectedTotal > 0 && (
							<Badge bg="light" text="dark">
								{formatCurrency(selectedTotal)}
							</Badge>
						)}
						{value.length > 0 && (
							<Button
								variant="link"
								size="sm"
								className="p-0"
								onClick={() => onChange([])}
							>
								{t('motornet_opt_clear', 'Svuota')}
							</Button>
						)}
						{groups.length > 1 && (
							<Button
								variant="light"
								size="sm"
								onClick={() => setAllExpanded(!allExpanded)}
								title={
									allExpanded
										? t('motornet_opt_collapse_all', 'Chiudi tutti i gruppi')
										: t('motornet_opt_expand_all', 'Apri tutti i gruppi')
								}
								aria-label={
									allExpanded
										? t('motornet_opt_collapse_all', 'Chiudi tutti i gruppi')
										: t('motornet_opt_expand_all', 'Apri tutti i gruppi')
								}
							>
								{allExpanded ? <FaAngleDoubleUp /> : <FaAngleDoubleDown />}
							</Button>
						)}
					</div>
				</div>

				{!visible.length ? (
					<div className="text-muted py-3">
						{t('motornet_opt_no_match', 'Nessun optional corrisponde alla ricerca')}
					</div>
				) : (
					<div style={{ maxHeight: '60vh', overflowY: 'auto' }}>
						<Table responsive hover className="align-middle mb-0">
							<thead
								style={{
									position: 'sticky',
									top: 0,
									zIndex: 2,
									background: 'var(--bs-body-bg, #fff)',
								}}
							>
								<tr>
									<th style={{ width: '3rem' }} />
									<th style={{ width: '8rem' }}>{t('code', 'Codice')}</th>
									<th>{t('description', 'Descrizione')}</th>
									<th className="text-end" style={{ width: '10rem' }}>
										{t('price', 'Prezzo')}
									</th>
								</tr>
							</thead>
							{groups.map((group) => {
								const expanded = isExpanded(group);
								const groupSelected = group.items.filter((i) =>
									selected.has(i.idAccessorio)
								).length;
								const allInGroup = groupSelected === group.items.length;
								return (
									<tbody key={group.name}>
										<tr className="table-light">
											<td>
												<Form.Check
													type="checkbox"
													id={`av-grp-${group.name}`}
													checked={allInGroup}
													ref={(el: HTMLInputElement | null) => {
														if (el)
															el.indeterminate = groupSelected > 0 && !allInGroup;
													}}
													onChange={(e) =>
														toggleMany(group.items, e.target.checked)
													}
													aria-label={group.name}
												/>
											</td>
											<td
												colSpan={3}
												style={{ cursor: 'pointer' }}
												onClick={() =>
													setToggles((prev) => ({
														...prev,
														[group.name]: !expanded,
													}))
												}
											>
												<div className="d-flex align-items-center gap-2 fw-semibold">
													{expanded ? (
														<FaChevronDown size={12} />
													) : (
														<FaChevronRight size={12} />
													)}
													<span>{group.name}</span>
													<Badge bg="secondary" pill>
														{group.items.length}
													</Badge>
													{groupSelected > 0 && (
														<Badge bg="primary" pill>
															{groupSelected}
														</Badge>
													)}
												</div>
											</td>
										</tr>
										{expanded &&
											group.items.map((item) => {
												const isChecked = selected.has(item.idAccessorio);
												const subtitle = subtitleOf(item);
												return (
													<tr
														key={item.idAccessorio}
														style={{ cursor: 'pointer' }}
														onClick={() => toggle(item, !isChecked)}
													>
														<td onClick={(e) => e.stopPropagation()}>
															<Form.Check
																type="checkbox"
																id={`av-opt-${item.idAccessorio}`}
																checked={isChecked}
																onChange={(e) => toggle(item, e.target.checked)}
																aria-label={item.descrizione || item.codice}
															/>
														</td>
														<td>{item.codice}</td>
														<td>
															<div className="d-flex align-items-center gap-2">
																<span>
																	{item.descrizione || item.descrizioneNormalizzata}
																</span>
																{item.isStandard && (
																	<Badge bg="secondary" pill>
																		{t('motornet_color_standard', 'Di serie')}
																	</Badge>
																)}
															</div>
															{subtitle && (
																<small className="text-muted d-block">
																	{subtitle}
																</small>
															)}
														</td>
														<td className="text-end">
															{item.prezzo != null
																? formatCurrency(item.prezzo)
																: item.isStandard
																	? formatCurrency(0)
																	: '—'}
														</td>
													</tr>
												);
											})}
									</tbody>
								);
							})}
						</Table>
					</div>
				)}
			</Card.Body>
		</Card>
	);
};

export default AutovistaOptionalsPicker;
